'use client';

import { getLanguageLabel } from '@/helpers/getLanguageLabels';
import type { AlternativeLanguage } from '@/types/AlternativeLanguage';
import { cn } from '@/utils/cn';

interface LanguageSwitcherProps {
    locale: string;
    alternativeLanguages: AlternativeLanguage[];
    fullLabel?: boolean;
    variant?: 'menu' | 'navbar';
    className?: string;
}

export default function LanguageSwitcher({
    locale,
    alternativeLanguages,
    fullLabel = false,
    variant = 'menu',
    className,
}: LanguageSwitcherProps) {
    const alternativeLanguagesWithoutDomain = alternativeLanguages.map((lang) => {
        try {
            const url = new URL(lang.url);
            return {
                language: lang.language,
                url: url.pathname + url.search + url.hash,
            };
        } catch (_e) {
            return {
                language: lang.language,
                url: lang.url,
            };
        }
    });

    if (variant === 'navbar') {
        // Only show other languages in navbar (not current)
        const otherLanguages = alternativeLanguagesWithoutDomain.filter(
            (lang) => lang.language !== locale
        );

        return (
            <div className={cn('language-switcher flex h-12 bg-haug-ivory-100', className)}>
                <div className="flex items-center font-neue-haas">
                    {otherLanguages.map((lang) => (
                        <a
                            key={lang.language}
                            href={lang.url}
                            className="px-3 py-2 font-bold text-sm transition-colors duration-200 hover:text-haug-coral-100"
                            style={{ height: '100%', display: 'flex', alignItems: 'center' }}
                        >
                            {lang.language === 'en' ? 'EN' : 'NL'}
                        </a>
                    ))}
                </div>
            </div>
        );
    }

    // Menu variant
    return (
        <ul className={cn('flex justify-center space-x-4', className)}>
            {alternativeLanguagesWithoutDomain.map((lang) => (
                <li key={lang.language}>
                    <a
                        key={lang.language}
                        href={lang.url}
                        className={cn(
                            'border border-haug-coral-100 px-2 py-2 text-base',
                            'font-bold text-sm',
                            locale === lang.language
                                ? 'bg-haug-ivory-100 text-haug-coral-100'
                                : 'text-haug-ivory-100/50 hover:border-haug-ivory-100 hover:text-haug-ivory-100'
                        )}
                    >
                        {getLanguageLabel(lang.language, fullLabel)}
                    </a>
                </li>
            ))}
        </ul>
    );
}
